import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import TransactionGridData from './components/TransactionGridData'
import TransactionGridFilter from './components/TransactionGridFilter'
import { useDispatch, useSelector } from 'react-redux'
import { getTransactionList } from 'app/redux/store/features/transactions'
import DateUtils from 'app/utils/components/DateUtils/DateUtils'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useLocation } from 'react-router-dom'

const Transactions = () => {
    const { state } = useLocation()
    const pageSize = 25
    const [filter, setFilter] = useState({
        termId: null,
        txnNumber: '',
        termYear: null,
        termNumber: null,
        startDate: DateUtils.getStartDate(30),
        endDate: DateUtils.getEndDate(),
        sort: 'DateTxn',
        isDescending: true,
    })
    const { transactions } = useSelector((state) => state.transactions)
    const [currentPage, setCurrentPage] = useState(0)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const dispatch = useDispatch()

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !transactions.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getTransactionList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function TransactionReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [TransactionList, setTransactionList] = React.useReducer(
        TransactionReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getTransactionList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getTransactionList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getTransactionList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        dispatch(getTransactionList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setTransactionList({ type: 'set-grid-filter', payload: value })
        },
        [setTransactionList]
    )

    const setFilterIconColor = React.useCallback(
        (value) => {
            setTransactionList({ type: 'set-filter-icon-color', payload: value })
        },
        [setTransactionList]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.2,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Transactions
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'primary.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: 0,
                            color:
                                TransactionList.filterIconColor && !TransactionList.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!TransactionList.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <TransactionGridFilter
                    mutation={applyFilter}
                    display={TransactionList.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <TransactionGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    apiCallInProgress={apiCallInProgress}
                    setApiCallInProgress={setApiCallInProgress}
                />
            </Grid>
        </JumboCardQuick>
    )
}

export default Transactions
